import * as React from 'react';
import { Grid, Button, Alert, TextLink, InfoIcon, TextPassage, Tooltip } from 'react-unity';
import RequestFormWrapper from '../common/wrappers/RequestFormWrapper';
import UserLookup from '../common/form-controls/UserLookup';
import TextAreaField from '../common/form-controls/TextareaField';
import { OnPremPORTypeInput } from './components/OnPremPORTypeInput';
import { OnPremRulesTable } from './components/OnPremRulesTable';
import TextInputField from '../../components/common/form-controls/TextInputField';
import PortOpeningRequestOnPrem from '../../models/entities/PortOpeningRequestOnPrem';
import { OnPremAbstractPORForm, AbstractPORFormState, AbstractPORFormProps, IPORStateForm } from './OnPremAbstractPORForm';
import { Validation, ValidationRule } from '../../utils/Validation';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import AlertModal from '../common/modals/AlertModal';
import SubmitPortOpeningRequestOnPrem from '../../models/viewModels/OnPremPortOpeningRequests/SubmitPortOpeningRequestOnPrem';
import SubmitPortOpeningRequestRuleOnPrem from '../../models/viewModels/OnPremPortOpeningRequests/SubmitPortOpeningRequestRuleOnPrem';
import ConfirmModal from '../common/modals/ConfirmModal';
import { RuleOperation } from '../../models/enums/POR/RuleOperation';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactButton from '../common/ReactButton';
import PortOpeningRuleOnPrem from '../../models/entities/PortOpeningRuleOnPrem';
import BusinessJustificationExample from '../../images/BusinessJustificationExample.png';
import { ApproverInput } from '../PortOpeningRequests/components';
import CartaInput from '../common/form-controls/Lookups/CartaInput';
import SelectField from '../common/form-controls/SelectField';

const businessJustificationTemplate = `Project Related: Yes/No
Project Name: <Project XYZ>

Project Background:
<Explain the background of the project, including the business need, goals, and any relevant context.>

Traffic Flow:
<Describe how data, users, or systems interact in the context of this particular POR. This could include network traffic, user access patterns, or system integration flows etc.>

Any other additional information:
<Specific request like implementation team etc>`;

interface PortOpeningRequestsCreateProps extends PortOpeningRequestsCreateWithParamsProps { }

interface PortOpeningRequestsCreateWithParamsProps extends AbstractPORFormProps {
	draftRequest?: PortOpeningRequestOnPrem;
}

interface StateForm extends IPORStateForm {
	selectedScope: string;
}

interface ILocationState {
	selectedRules?: PortOpeningRuleOnPrem[]
}

interface PortOpeningRequestsCreateWithParamsState extends AbstractPORFormState<StateForm> {
	cancelModalVisible: boolean;
	changeScopeModalVisible: boolean;
	newScope: string;
	implementedRules: PortOpeningRuleOnPrem[];
	loading: boolean;
	subToInternetWarningVisible: boolean;
	loadingImplementedRules: boolean;
	failedImplementedRules: boolean;
	selectedFromExisting: string[];
}

class PortOpeningRequestsCreateWithParams extends OnPremAbstractPORForm
	<PortOpeningRequestsCreateWithParamsProps, PortOpeningRequestsCreateWithParamsState> {

	constructor(props) {
		super(props);
		this.state = {
			stateForm: {
				selectedScope: '',
				contact: null,
				connectionId: null,
				businessJustification: businessJustificationTemplate,
				cartaIds: [],
				ownerDelegate: null,
				ruleList: []
			},
			endModal: {
				visible: false,
			},
			submissionAlert: {
				visible: false,
			},
			submissionWarned: false,
			operationInProgress: false,
			cancelModalVisible: false,
			changeScopeModalVisible: false,
			newScope: null,
			implementedRules: [],
			loading: true,
			subToInternetWarningVisible: true,
			loadingImplementedRules: true,
			failedImplementedRules: false,
			selectedFromExisting: [],
			loadingTracker: false,
		};
		this.initFormModel();
	}

	initFormModel() {
		super.initFormModel();
		this.formModel.addField('direction', {
			getValue: () => this.state.stateForm.selectedScope,
			validation: {
				required: true,
			},
		});

		this.formModel.fields.connectionId.validation = new Validation({
			required: () => this.state.stateForm.selectedScope === PortOpeningRequestsDirection.DomainToConnection.name,
			rules:
				[
					{
						assert: () => /^[0-9]*$/.test(this.state.stateForm.connectionId?.toString() ?? ''),
						message: 'Only numbers are valid values'
					},
					{
					assert: () => this.state.stateForm.connectionId >= 10000,
					message: 'Tracker ID must be greater than 10000',
				},
				]
		});

		this.formModel.fields.portOpeningRules.getValue =
			() => this.state.stateForm.ruleList.map(rule => new SubmitPortOpeningRequestRuleOnPrem(rule));

		this.formModel.fields.businessJustification.validation.rules.push(
			this.IsBusinessJustificationTemplateModified()
		)
		this.formModel.addField('cartaId', {
			getValue: () => this.state.stateForm.cartaIds,
		});

		this.formModel.addField('portOpeningRequestOnPremId', {
			getValue: () => this.props.draftRequest?.id,
		});
		
	}

	IsBusinessJustificationTemplateModified = (): ValidationRule => ({
		assert: () => {
			return this.state.stateForm.businessJustification.localeCompare(businessJustificationTemplate) != 0;
		},
		message: () => {
			return `You must edit Business Justification Template.`;
		}
	});

	stateFormFromRequest(por: PortOpeningRequestOnPrem): StateForm {
		let obj = {
			...super.stateFormFromRequest(por),
			selectedScope: por.direction?.name || '',
		};
		return { ...obj, ruleList: obj.ruleList.map(rule => { return { ...rule } }) }
	}

	async componentDidMount() {
		window.scroll(0, 0);
		if (this.props.draftRequest)
			this.setState({ stateForm: this.stateFormFromRequest(this.props.draftRequest) });
		this.setState({
			loading: false
		});
	}

	returningRoute = () => {
		return `/OnPremPortOpeningRequests`;
	};

	handleCancel = () => {
		this.setState({
			cancelModalVisible: false,
			operationInProgress: false
		});
		this.props.navigate('/onPremportOpeningRequests');
	};

	

	handleSubmit = async () => {
		this.setSubmissionAlertInProgress('Submitting your request...');
		try {
			if (this.requiresApproverVerification(this.state.stateForm.selectedScope)) {
				const isVerified = await this.approverLevelVerified();
				if (!isVerified)
					return;
			}
			let model: SubmitPortOpeningRequestOnPrem;
			model = this.formModel.create(SubmitPortOpeningRequestOnPrem);
			let request: PortOpeningRequestOnPrem;
			request = await this.portOpeningRequestServiceOnPrem.create(model);
			this.handleEndModal(
				`Your request was submitted successfully.`,
				'success',
				5000,
				this.returningRoute()
			);
		} catch (err) {
			if (err?.status == 403 && (err?.response.headers.get("content-type") == "application/json" ? JSON.stringify(err?.response.data).includes("Application-Gateway") : err?.response?.data?.includes("Application-Gateway"))) {
				this.setSubmissionAlertError("The content you input contains some sensitive information.Please email to GSC-EMIT-ITOPS-HP-PLATFORMS-AZURE-CHINA.UG@exxonmobil.com and provide the UTC timestamp[" + new Date().toISOString() + "]. We will get back to you during our working hours (8:00 - 16:00 UTC + 8:00).");
			} else {
				this.setSubmissionAlertError(err?.response?.data.message ?? 'An error occured when processing your request. Please, try again later.');
			}
		} finally {
			this.setState({
				operationInProgress: false,
			});
		}
	};

	handleScopeChange = (event: any) => {
		if (this.state.stateForm.ruleList.length > 0) {
			this.setState({ changeScopeModalVisible: true, newScope: event.target.value });
		} else {
			this.handleStateFormChange('selectedScope', event.target.value);
			this.handleStateFormChange('connectionId', null);
			this.handleStateFormChange('approver', null);
		}

	};

	handleTrackerIdChange = async (event: any) => {
		this.stateFormHandler().connectionId.onChange(event);
	};

	confirmScopeChange = () => {
		this.handleStateFormChange('selectedScope', this.state.newScope);
		this.handleStateFormChange('ruleList', []);
		this.handleStateFormChange('approver', null);
		this.setState({
			changeScopeModalVisible: false,
			newScope: null
		});
	};

	ruleIsFromSelectedDirection(rule: PortOpeningRuleOnPrem): boolean {
		return rule.direction.name === this.state.stateForm.selectedScope;
	}

	ruleIsAddedAlready(rule: PortOpeningRuleOnPrem): boolean {
		return this.state.stateForm.ruleList
			.map(editedRule => editedRule.azureName).includes(rule.azureName);
	}

	getImplementedRulesToShow(): PortOpeningRuleOnPrem[] {
		return this.state.implementedRules.filter(rule =>
			this.ruleIsFromSelectedDirection(rule)
			&& !this.ruleIsAddedAlready(rule)
			&& !this.state.selectedFromExisting.includes(rule.name)
		);
	}

	setSelectedFromExisting = (rulesName: string[]) => {
		this.setState({
			selectedFromExisting: rulesName
		})
	}

	isValid = () => {
		return (this.formModel.isValid() && (this.props.draftRequest || !this.wasNotEdited()))
	}

	isKeepOrRemove = (rule) => rule.operation == RuleOperation.Keep || rule.operation == RuleOperation.Remove;

	// attestationIsValid = () => {
	// 	return this.props.draftRequest?.isAttestation &&
	// 		this.state.stateForm.ruleList.every(this.isKeepOrRemove)
	// }

	wasNotEdited = () => {
		return (this.state.stateForm.ruleList.some((rule) => rule.azureName != undefined));
	}

	Icon = () => {
		return <InfoIcon className="info-icon em-c-field__label" />;
	}

	render() {
		return (
			<>

				<RequestFormWrapper
					title={"New Port Opening Request"}
					linkTree={this.getLinkTree()}
					loading={this.state.loading}
				>
					<p>
						For more information about PORs {' '}
						<TextLink className='em-u-margin-right-none' external target="_blank" href='https://appwiki.xom.cloud/docs/ConnectedAzure/PortOpeningRequests.html'>
							Click here
						</TextLink>
					</p>
					{this.state.submissionAlert.visible &&
						<Alert
							variant={this.state.submissionAlert.variant}
							onClose={this.handleSubmissionAlertClose}
						>
							{this.state.submissionAlert.text}
						</Alert>}
					<Grid variant="halves">
						<Grid.Item>
							<OnPremPORTypeInput
								scope={{
									value: this.state.stateForm.selectedScope,
									validation: this.formModel.fields.direction.validation,
									onChange: this.handleScopeChange,
									disabled: this.state.operationInProgress
								}}
							/>
						</Grid.Item>
					</Grid>
					<Grid variant="2-up">
						<Grid.Item>
							<UserLookup
								label="Technical contact"
								{...this.stateFormHandler().contact}
								disabled={this.state.operationInProgress}
							/>
						</Grid.Item>
						{this.requiresApproverVerification(this.state.stateForm.selectedScope)
							?
							<Grid.Item>
								<ApproverInput
									{...this.stateFormHandler().approver}
									disabled={this.state.operationInProgress} //|| this.props.draftRequest?.isAttestation}
								/>
							</Grid.Item>
							:
							<Grid.Item>
								<SelectField
									label="Approver"
									className="full-width"
									{...this.stateFormHandler().ownerDelegate}
									disabled={
										this.state.operationInProgress ||
										this.stateFormHandler().ownerDelegate.options.length == 0 ||
										this.state.loadingTracker
									}
									// isLoading={this.stateFormHandler().ownerDelegate.options.length == 0}
									note={
										this.stateFormHandler().ownerDelegate.options.length > 0 ? (
											<></>
										) : (
											<span id="firewallError">
												Approver is being pulled from NCDB based on the Tracker ID and active employee status, only could be selected if user has @exxonmobil.com.cn account. goto/NCDB to update role assignments.
											</span>
										)
									}
								/>
							</Grid.Item>
						}
					</Grid>
					<Grid variant="2-up">
						{this.requiresApproverVerification(this.state.stateForm.selectedScope) ? (
							<Grid.Item>
								<CartaInput
									{...this.stateFormHandler().cartaIds}
									disabled={this.state.operationInProgress}
									oneOptionOnly={this.state.stateForm.cartaIds.length > 0}
									hideNotes={this.state.operationInProgress}
								/>
							</Grid.Item>
						) : (
							<Grid.Item>
								<TextInputField
									label="Tracker Id"
									{...this.stateFormHandler().connectionId}
									disabled={this.state.operationInProgress}
									scope={this.state.stateForm.selectedScope}
									readOnly={this.state.loadingTracker}
									onChange={this.handleTrackerIdChange}
									note={
										this.state.loadingTracker
											? "Validating..."
											: this.state.failedTracker
											? this.state.errorTracker
											: ""
									}
								/>
							</Grid.Item>
						)}
					</Grid>
					<Grid.Item>
						<TextPassage>
							<Tooltip className="em-u-margin-none" variant="above">
								<Tooltip.Passage >
									<div id="text-passage">
										Business Justification <InfoIcon className="info-icon em-c-field__label" />
									</div>
								</Tooltip.Passage>
								<Tooltip.Content className="business-justification-example"><img className="business-justification-example" src={BusinessJustificationExample} /></Tooltip.Content>
							</Tooltip>

						</TextPassage>
						<TextAreaField
							label=''
							{...this.stateFormHandler().businessJustification}
							fitContent={true}
							disabled={this.state.operationInProgress}
						/>
					</Grid.Item>
					<OnPremRulesTable
						{...this.stateFormHandler().ruleset}
						porDirection={new PortOpeningRequestsDirection().fromName(this.state.stateForm.selectedScope)}
					/>
					<Grid variant="4-up">
						<Grid.Item>
							<Button
								variant="secondary"
								onClick={() => {
									this.setState({
										cancelModalVisible: true,
										operationInProgress: true,
									});
								}}
								disabled={this.state.operationInProgress}
							>
								Cancel Request
							</Button>
						</Grid.Item>
						<Grid.Item />
						<Grid.Item>
							<ReactButton
								variant="primary"
								disabled={!this.isValid()}
								isLoading={this.state.operationInProgress}
								handleUpdateSubmit={this.handleSubmit}
								loadingVariant="secondary"
								name="Submit Request"
							/>
						</Grid.Item>
					</Grid>
				</RequestFormWrapper>
				<ConfirmModal
					visible={this.state.cancelModalVisible}
					title='Cancel Request?'
					question="Are you sure you want to cancel this request? This action cannot be undone."
					confirmButton={{
						label: 'Yes',
						props: {
							variant: 'primary',
							color: 'negative'
						}
					}}
					cancelButton={{
						label: 'No',
					}}
					onConfirm={this.handleCancel}
					onCancel={() => {
						this.setState({
							cancelModalVisible: false,
							operationInProgress: false
						});
					}}
				/>
				<ConfirmModal
					visible={this.state.changeScopeModalVisible}
					title="Change scope"
					question="Are you sure you want to continue? If so, the ruleset will be cleared."
					confirmButton={{
						label: 'Yes',
					}}
					onConfirm={this.confirmScopeChange}
					onCancel={() => {
						this.setState({
							changeScopeModalVisible: false
						});
					}}
				/>
				<AlertModal
					{...this.state.endModal}
					willTimeout={false}
					onClose={this.endAndRedirect}
				/>
			</>
		);
	}
}

const PortOpeningRequestsCreate = (props: PortOpeningRequestsCreateProps) => {

	return <PortOpeningRequestsCreateWithParams
		draftRequest={props.draftRequest}
		location={useLocation()}
		navigate={useNavigate()}
		params={useParams()} />;
};

export default PortOpeningRequestsCreate;